import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { toast } from '@/components/toast';
import Copy from '@/images/Copy.svg';
import theme from '@/theme';
import { Button } from '@de-id/ui-components';
import styled from '@emotion/styled';
import { useFormContext } from 'react-hook-form';
import { WebhookFormValues } from '../../../types';
import { getFlattenedErrors } from '../../../utils/errors';

const JsonContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

const TextAreaContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    position: relative;
    border: 1px solid #dddddd;
    border-radius: 8px;
    overflow: hidden;
`;

const LineNumbers = styled.div`
    color: #afafaf;
    padding: 8px;
    font-family: monospace;
    font-size: 14px;
    line-height: 1.5;
    text-align: right;
    user-select: none;
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
    border-right: 1px solid #dddddd;
    width: 30px; /* Fixed width for line numbers */
    flex-shrink: 0;
`;

const StyledTextArea = styled.textarea`
    background-color: transparent;
    color: ${theme.colors.primary.main};
    padding: 8px;
    border-radius: 8px;
    white-space: pre-wrap;
    word-break: break-all;
    font-family: monospace;
    font-size: 14px;
    line-height: 1.5;
    flex: 1;
    overflow: auto;
    border: none;
    resize: none;
    outline: none;
    padding-left: 8px;
`;

const ContentWrapper = styled.div`
    display: flex;
    flex: 1;
    position: relative;
`;

const CopyButton = styled(Button)`
    position: absolute;
    top: 8px;
    right: 8px;
    width: 24px;
    height: 24px;
    padding: 4px;

    svg {
        margin-left: 2px;
    }
`;

const MessageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 0;
    overflow: auto;

    color: ${theme.colors.error[650]};
    font-family: Manrope;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px; /* 133.333% */
`;

const jsonReplacer = (mode: 'edit' | 'create') => (key: string, value: string) => {
    if (mode === 'edit' && (key === 'token' || key === 'api_key' || key === 'password')) {
        return '***';
    }
    return value;
};

interface Props {
    mode: 'edit' | 'create';
}

export const JsonContent = ({ mode }: Props) => {
    const { watch, formState } = useFormContext<WebhookFormValues>();
    const formValues = watch();
    const { errors } = formState;

    const currentJsonString = JSON.stringify(formValues, jsonReplacer(mode), 2);
    const lineCount = currentJsonString.split('\n').length;
    const lineNumbers = Array.from({ length: lineCount }, (_, i) => i + 1).join('\n');

    const handleCopy = () => {
        toast.success({ title: 'copied to clipboard' });
        navigator.clipboard.writeText(currentJsonString);
    };

    const flattenedErrors = getFlattenedErrors(errors);

    return (
        <JsonContentContainer>
            <TextAreaContainer>
                <ContentWrapper>
                    <LineNumbers>{lineNumbers}</LineNumbers>
                    <StyledTextArea value={currentJsonString} readOnly />
                </ContentWrapper>
                <CopyButton size="small" color="outlined" onClick={handleCopy}>
                    <MaskedIcon src={Copy} size="16px" />
                </CopyButton>
            </TextAreaContainer>
            {flattenedErrors.length > 0 && (
                <MessageContainer>
                    {flattenedErrors.map((error, index) => (
                        <p key={index}>{`${error.name}: ${error.message}`}</p>
                    ))}
                </MessageContainer>
            )}
        </JsonContentContainer>
    );
};
